import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Container, Row, Col } from "reactstrap";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  getApiUrl,
  getMediaUrl,
  siteConfig,
} from "../utils/site";

const getImageUrl = (url) =>
  url ? getMediaUrl(url) : "/image.jpg";

const BlogDetails = () => {
  const { id } = useParams();

  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogRes, recentRes] = await Promise.all([
          axios.get(getApiUrl(`/api/blogs/${id}`)),
          axios.get(getApiUrl("/api/blogs")),
        ]);

        setBlog(blogRes.data);

        setRecentBlogs(
          recentRes.data.blogs || recentRes.data || []
        );
      } catch (err) {
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;

      const docHeight =
        document.documentElement.scrollHeight -
        window.innerHeight;

      setProgress(
        docHeight > 0
          ? (scrollTop / docHeight) * 100
          : 0
      );
    };

    window.addEventListener("scroll", handleScroll);

    return () =>
      window.removeEventListener(
        "scroll",
        handleScroll
      );
  }, []);

  if (loading) {
    return (
      <div
        className="roha-page"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p className="roha-eyebrow">
          Loading article...
        </p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div
        className="roha-page"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ color: "var(--color-muted)" }}>
          Article not found.
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {blog.title} | Roha Jewellery Journal
        </title>

        <meta
          name="description"
          content={String(blog.content || "")
            .replace(/<[^>]+>/g, "")
            .slice(0, 160)}
        />

        <link
          rel="canonical"
          href={`${siteConfig.url}/blogs/${id}`}
        />
      </Helmet>

      {/* PROGRESS BAR */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          width: `${progress}%`,
          background:
            "linear-gradient(90deg, var(--color-gold), var(--color-gold-pale))",
          zIndex: 9999,
          transition: "width 0.1s linear",
        }}
      />

      <main className="roha-page">
        {/* HERO */}
        <section
          style={{
            position: "relative",
            minHeight: "55vh",
            display: "flex",
            alignItems: "flex-end",
            overflow: "hidden",
            background: "var(--color-dark)",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
            }}
          >
            <img
              src={getImageUrl(blog.image_url)}
              alt={blog.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.45,
              }}
            />

            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(0deg, rgba(6,14,13,0.98) 0%, rgba(6,14,13,0.5) 60%, transparent 100%)",
              }}
            />
          </div>

          <Container
            style={{
              position: "relative",
              zIndex: 1,
              paddingBottom: "4rem",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="roha-eyebrow">
                Roha Journal
              </p>

              <h1
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize:
                    "clamp(2rem,4vw,3.5rem)",
                  fontWeight: 400,
                  color: "var(--color-heading)",
                  maxWidth: 800,
                  lineHeight: 1.15,
                  marginBottom: "1rem",
                }}
              >
                {blog.title}
              </h1>

              <p
                style={{
                  color: "var(--color-muted)",
                  fontSize: "0.88rem",
                }}
              >
                {blog.author ||
                  "Roha Jewellery"}{" "}
                ·{" "}
                {blog.created_at
                  ? new Date(
                      blog.created_at
                    ).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )
                  : ""}
              </p>
            </motion.div>
          </Container>
        </section>

        {/* CONTENT */}
        <section className="roha-section">
          <Container>
            <Row className="g-5">
              <Col lg="8">
                <article style={{ maxWidth: 720 }}>
                  {String(blog.content || "")
                    .split("\n")
                    .map((para, i) => {
                      if (!para.trim()) return null;

                      const isHeading =
                        para.trim().length < 60 &&
                        para.trim() ===
                          para.trim().toUpperCase();

                      return isHeading ? (
                        <motion.h2
                          key={i}
                          initial={{
                            opacity: 0,
                            y: 20,
                          }}
                          whileInView={{
                            opacity: 1,
                            y: 0,
                          }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.5,
                          }}
                          style={{
                            fontFamily:
                              "var(--font-heading)",
                            fontSize: "1.8rem",
                            color:
                              "var(--color-heading)",
                            marginTop: "2.5rem",
                            marginBottom: "1rem",
                          }}
                        >
                          {para}
                        </motion.h2>
                      ) : (
                        <motion.p
                          key={i}
                          initial={{
                            opacity: 0,
                            y: 16,
                          }}
                          whileInView={{
                            opacity: 1,
                            y: 0,
                          }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.5,
                            delay: 0.05,
                          }}
                          style={{
                            fontSize: "1.05rem",
                            color:
                              "var(--color-text)",
                            lineHeight: 1.9,
                            marginBottom: "1.25rem",
                          }}
                        >
                          {para}
                        </motion.p>
                      );
                    })}
                </article>

                <div
                  style={{
                    marginTop: "3rem",
                    paddingTop: "2rem",
                    borderTop:
                      "1px solid var(--border-gold)",
                  }}
                >
                  <Link
                    to="/blogs"
                    className="roha-text-link"
                  >
                    Back to Journal
                  </Link>
                </div>
              </Col>

              <Col lg="4">
                <aside
                  style={{
                    position: "sticky",
                    top: "120px",
                    display: "grid",
                    gap: "1.5rem",
                  }}
                >
                  <div className="roha-feature-card">
                    <p className="roha-eyebrow">
                      About the Author
                    </p>

                    <h4
                      style={{
                        color:
                          "var(--color-heading)",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {blog.author ||
                        "Roha Jewellery Expert"}
                    </h4>

                    <p
                      style={{
                        fontSize: "0.88rem",
                        color:
                          "var(--color-muted)",
                        margin: 0,
                      }}
                    >
                      Roha Jewellery's editorial
                      team shares stories about
                      Ethiopian gemstones, luxury
                      jewellery, and Habesha
                      craftsmanship.
                    </p>
                  </div>

                  {recentBlogs
                    .filter((b) => b.id !== blog.id)
                    .slice(0, 4).length > 0 && (
                    <div className="roha-feature-card">
                      <p className="roha-eyebrow">
                        Recent Articles
                      </p>

                      <div
                        style={{
                          display: "grid",
                          gap: "0.75rem",
                        }}
                      >
                        {recentBlogs
                          .filter(
                            (b) => b.id !== blog.id
                          )
                          .slice(0, 4)
                          .map((item) => (
                            <Link
                              key={item.id}
                              to={`/blogs/${item.id}`}
                              style={{
                                color:
                                  "var(--color-heading)",
                                fontSize: "0.9rem",
                                lineHeight: 1.4,
                                display: "block",
                                transition:
                                  "color 0.2s",
                              }}
                            >
                              {item.title}
                            </Link>
                          ))}
                      </div>
                    </div>
                  )}

                  <div className="roha-feature-card">
                    <p className="roha-eyebrow">
                      Explore Roha
                    </p>

                    <div
                      style={{
                        display: "grid",
                        gap: "0.75rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Link
                        to="/products"
                        className="roha-button roha-button--solid"
                        style={{
                          textAlign: "center",
                        }}
                      >
                        Shop Collection
                      </Link>

                      <Link
                        to="/design-request"
                        className="roha-button roha-button--ghost"
                        style={{
                          textAlign: "center",
                        }}
                      >
                        Bespoke Design
                      </Link>
                    </div>
                  </div>
                </aside>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    </>
  );
};

export default BlogDetails;